import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { alpha } from "@mui/material/styles";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import { useTranslation } from "../../hooks";
import { getPatientDisplayName } from "../../utils/appointmentFormat";
import { AddPatientDialog } from "./AddPatientDialog";
import { PortalCard } from "./ui/PortalCard";
import { PortalSection } from "./ui/PortalSection";
import { PortalEmptyState } from "./ui/PortalEmptyState";
function initialsOf(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return "?";
    const first = parts[0]?.[0] ?? "";
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
    return (first + last).toUpperCase();
}
export function PatientRecordsSection({ patients, addressId, onAdded, }) {
    const { t } = useTranslation("portal");
    const [adding, setAdding] = React.useState(false);
    const addButton = (_jsx(Button, { size: "small", startIcon: _jsx(PersonAddAltRoundedIcon, { sx: { fontSize: 16 } }), onClick: () => setAdding(true), sx: { px: 1.25, py: 0.375, borderRadius: 999, fontSize: 12.5 }, children: t("addMember", "Add a family member") }));
    return (_jsxs(PortalSection, { title: t("yourRecords", "Your records at this clinic"), hint: t("recordsHint", "Everyone registered under this phone number."), action: patients.length > 0 ? addButton : undefined, children: [patients.length === 0 ? (_jsx(PortalCard, { children: _jsx(PortalEmptyState, { icon: _jsx(GroupsRoundedIcon, {}), title: t("noRecords", "No patient records are registered under this number yet."), action: addButton }) })) : (_jsx(Box, { sx: {
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    gap: 1.25,
                }, children: patients.map((patient) => {
                    const name = getPatientDisplayName(undefined, {
                        firstName: patient.firstName ?? undefined,
                        lastName: patient.lastName ?? undefined,
                    });
                    const meta = [
                        patient.mrn
                            ? t("mrn", "MRN {{mrn}}", { mrn: patient.mrn })
                            : null,
                        patient.gender,
                    ]
                        .filter(Boolean)
                        .join(" · ");
                    return (_jsxs(PortalCard, { sx: {
                            px: 1.75,
                            py: 1.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                        }, children: [_jsx(Box, { "aria-hidden": "true", sx: (theme) => ({
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 36,
                                    height: 36,
                                    flexShrink: 0,
                                    borderRadius: "50%",
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: theme.palette.primary.main,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                }), children: initialsOf(name) }), _jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 600, wordBreak: "break-word" }, children: name }), meta ? (_jsx(Typography, { variant: "caption", sx: { color: "text.secondary", wordBreak: "break-word" }, children: meta })) : null] })] }, patient.id));
                }) })), _jsx(AddPatientDialog, { open: adding, addressId: addressId, onClose: () => setAdding(false), onAdded: onAdded })] }));
}
